import { useMemo } from 'react'
import { createColumnHelper } from '@atbay/cyber-components'
import Show from '@/ui/components/helpers/Show'
import Typography from '@/ui/components/typography'
import { useCustomTheme } from '@/core/theme'
import {
    TAllIntegrations,
    TIntegration,
} from '@/core/modules/integrations/integrations'
import { SEVERITY_LEVELS } from '@/core/modules/issues/constants'
import { SimpleMenu } from '@/ui/components/SimpleMenu'
import { MenuButton } from '@/ui/components/buttons/MenuButton'
import { SkeletonBar } from '@/ui/components/SkeletonBar'
import { IntegrationStatus } from './IntegrationStatus'
import { getDateString } from '@/ui/utils/utils'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'
import { countIssuesBySeverity } from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import {
    ActionsCell,
    ClickableCell,
    StyledIntegrationsTable,
    IssueCountItem,
    IssueCountWrapper,
    TableWrapper,
} from './IntegrationsTable.styles'
import {
    TIntegrationsTableRow,
    useIntegrationsTable,
} from './hooks/useIntegrationsTable'
import { getIntegrationsTableRowId } from './IntegrationsTable.utils'

export type IntegrationsTableProps = {
    integrations: TIntegration[]
    dataTestId?: string
    name?: string
    type: TAllIntegrations
}

const IntegrationsTable = ({
    integrations,
    dataTestId,
    name,
    type,
}: IntegrationsTableProps) => {
    const theme = useCustomTheme()
    const {
        rows,
        handleRowClick,
        activeAssetId,
        openMenuId,
        toggleMenu,
        closeMenu,
        getMenuItems,
        isAssetsLoading,
    } = useIntegrationsTable(integrations, type)
    const columnHelper = createColumnHelper<TIntegrationsTableRow>()

    const columns = useMemo(
        () => [
            {
                ...columnHelper.accessor(
                    (row) => row.integration.external_identifier,
                    {
                        id: 'accountName',
                        cell: (info) => (
                            <ClickableCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                isClickable={!!info.row.original.asset}
                                data-testid={`${name}-integrations-table-account-name`}
                            >
                                <Typography
                                    variant="s-medium"
                                    color={theme.colors.greys[700]}
                                >
                                    {info.row.original.integration.type ===
                                    'crowdstrike'
                                        ? info.row.original.integration
                                              .display_name
                                        : info.cell.getValue()}
                                </Typography>
                            </ClickableCell>
                        ),
                        header: () => (
                            <Typography variant="s-bold">
                                Account Name
                            </Typography>
                        ),
                        enableSorting: false,
                    }
                ),
                width: '24%',
            },
            {
                ...columnHelper.accessor(
                    (row) => row.integration.activated_at,
                    {
                        id: 'createdAt',
                        cell: (info) => (
                            <ClickableCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                isClickable={!!info.row.original.asset}
                                data-testid={`${name}-integrations-table-created-date`}
                            >
                                <Typography
                                    variant="s-medium"
                                    color={theme.colors.greys[700]}
                                >
                                    {getDateString(info.cell.getValue() ?? '')}
                                </Typography>
                            </ClickableCell>
                        ),
                        header: () => (
                            <Typography variant="s-bold">
                                Created Date
                            </Typography>
                        ),
                        enableSorting: false,
                    }
                ),
                width: '18%',
            },
            {
                ...columnHelper.accessor((row) => row?.asset?.last_seen, {
                    id: 'lastSeen',
                    cell: (info) => (
                        <ClickableCell
                            onClick={() => handleRowClick(info.row, info.table)}
                            isClickable={!!info.row.original.asset}
                            data-testid={`${name}-integrations-table-last-seen`}
                        >
                            {isAssetsLoading ? (
                                <SkeletonBar
                                    dataTestId={`${name}-integrations-table-last-seen-skeleton`}
                                />
                            ) : (
                                <Typography
                                    variant="s-medium"
                                    color={theme.colors.greys[700]}
                                >
                                    {getDateString(info.cell.getValue() ?? '')}
                                </Typography>
                            )}
                        </ClickableCell>
                    ),
                    header: () => (
                        <Typography variant="s-bold">Last Seen</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '18%',
            },
            {
                ...columnHelper.accessor((row) => row.integration.status, {
                    id: 'status',
                    cell: (info) => {
                        const integration = info.row.original.integration
                        return (
                            <ClickableCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                isClickable={!!info.row.original.asset}
                                data-testid={`${name}-integrations-table-status-${integration.status}`}
                            >
                                <IntegrationStatus
                                    status={integration.status}
                                    type={type}
                                />
                            </ClickableCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-bold">Status</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '14%',
            },
            {
                ...columnHelper.accessor((row) => row.asset?.issues, {
                    id: 'issues',
                    cell: (info) => {
                        const issuesCount = countIssuesBySeverity(
                            info.row.original?.asset?.issues ?? []
                        )
                        return (
                            <ClickableCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                isClickable={!!info.row.original.asset}
                                data-testid={`${name}-integrations-table-issues`}
                            >
                                {isAssetsLoading ? (
                                    <SkeletonBar
                                        dataTestId={`${name}-integrations-table-issues-skeleton`}
                                    />
                                ) : (
                                    <IssueCountWrapper>
                                        {SEVERITY_LEVELS.map((severity) => (
                                            <Show
                                                key={severity}
                                                when={issuesCount?.[severity]}
                                            >
                                                <IssueCountItem
                                                    className={`count ${severity}`}
                                                    data-testid="integration-issues-severity-count"
                                                >
                                                    <SeverityIcon
                                                        severity={severity}
                                                        dataTestId={`severity-${severity}`}
                                                    />
                                                    <Typography variant="s-regular">
                                                        {
                                                            issuesCount?.[
                                                                severity
                                                            ]
                                                        }
                                                    </Typography>
                                                </IssueCountItem>
                                            </Show>
                                        ))}
                                    </IssueCountWrapper>
                                )}
                            </ClickableCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-bold">Open Issues</Typography>
                    ),
                    enableSorting: false,
                }),
                width: '18%',
            },
            {
                ...columnHelper.display({
                    id: 'actions',
                    cell: (info) => {
                        const integration = info.row.original.integration
                        const asset = info.row.original?.asset
                        const rowKey = asset?.id ?? integration.id
                        const isMenuOpen = openMenuId === rowKey
                        return (
                            <ActionsCell
                                data-testid={`${name}-integrations-table-actions`}
                            >
                                <SimpleMenu
                                    data-testid={`${name}-integrations-table-menu`}
                                    onActivate={() => toggleMenu(rowKey)}
                                    open={isMenuOpen}
                                    onClose={closeMenu}
                                    activator={
                                        <MenuButton
                                            isOpen={isMenuOpen}
                                            dataTestId={`${name}-integrations-table-menu-button-${info.row.index}`}
                                        />
                                    }
                                    openLeft
                                    items={getMenuItems(integration) as any}
                                />
                            </ActionsCell>
                        )
                    },
                    header: () => null,
                }),
                width: '8%',
            },
        ],
        [
            theme,
            name,
            type,
            openMenuId,
            handleRowClick,
            toggleMenu,
            closeMenu,
            getMenuItems,
            isAssetsLoading,
        ]
    )

    if (rows.length === 0) return null

    return (
        <TableWrapper>
            <StyledIntegrationsTable
                data-testid={dataTestId}
                borderColor={theme.colors.greys[200]}
                columns={columns}
                data={rows}
                activeRow={activeAssetId}
                getRowId={getIntegrationsTableRowId}
                // @ts-ignore
                tableHeight="fit-content"
            />
        </TableWrapper>
    )
}

export default IntegrationsTable
